import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { db } from "../../firebase";
import { collection, getDocs, query, where } from "firebase/firestore";
import { useAuth } from "../../AuthContext";

function formatTime(time24) {
  if (!time24) return "";
  const [h, m] = time24.split(":");
  const hour = parseInt(h, 10);
  const period = hour >= 12 ? "PM" : "AM";
  const hour12 = hour % 12 === 0 ? 12 : hour % 12;
  return `${hour12}:${m} ${period}`;
}

export default function CharityDashboard() {
  const { user, userBars } = useAuth();
  const myBars = userBars.filter((bar) => bar.charityId === user?.uid);

  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadEvents = async () => {
      const barIds = myBars.map((b) => b.id);
      if (barIds.length === 0) {
        setEvents([]);
        setLoading(false);
        return;
      }
      setLoading(true);
      const q = query(collection(db, "events"), where("barId", "in", barIds.slice(0, 10)));
      const snap = await getDocs(q);
      setEvents(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setLoading(false);
    };
    loadEvents();
  }, [userBars]);

  // First-run guidance: if this charity hasn't linked a venue yet, send
  // them straight to Claim Bar instead of a blank, all-zeros Overview page.
  // NOTE: double-check this route matches your actual CharityLayout.jsx —
  // built by assuming the same pattern as the owner side.
  if (myBars.length === 0) {
    return <Navigate to="/charity/claim-bar" replace />;
  }

  // FUTURE (once the pilot ends and a real charity subscription page exists):
  // if (!PILOT_MODE && !hasActiveSubscription) {
  //   return <Navigate to="/charity/subscription" replace />;
  // }

  const charityName = myBars.find((b) => b.gaming?.charityName)?.gaming?.charityName;
  const barNameById = Object.fromEntries(myBars.map((b) => [b.id, b.name || "Unnamed Bar"]));

  const weeklyEvents = events.filter((e) => e.recurrence === "weekly");
  const oneTimeEvents = events
    .filter((e) => e.recurrence === "one-time")
    .sort((a, b) => (a.date > b.date ? 1 : -1));

  return (
    <>
      <h2 style={{ marginBottom: "20px" }}>{charityName || "Your Organization"}</h2>

      <div className="metrics-grid">
        <div className="metric-card">
          <h2>Linked Venues</h2>
          <p>{myBars.length}</p>
        </div>
        <div className="metric-card">
          <h2>Recurring Events</h2>
          <p>{weeklyEvents.length}</p>
        </div>
        <div className="metric-card">
          <h2>Upcoming One-Time Events</h2>
          <p>{oneTimeEvents.length}</p>
        </div>
      </div>

      <section className="events-section">
        <h2>Recurring Events</h2>
        {loading ? (
          <p style={{ color: "#B8B0C8" }}>Loading...</p>
        ) : weeklyEvents.length === 0 ? (
          <p style={{ color: "#B8B0C8", marginBottom: "20px" }}>No recurring events yet.</p>
        ) : (
          weeklyEvents.map((ev) => (
            <div className="event-card" key={ev.id}>
              <h3>{ev.title}</h3>
              <p>Every {ev.dayOfWeek} at {formatTime(ev.time)} — {barNameById[ev.barId]}</p>
            </div>
          ))
        )}
      </section>

      <section className="events-section">
        <h2>Upcoming One-Time Events</h2>
        {oneTimeEvents.length === 0 ? (
          <p style={{ color: "#B8B0C8" }}>No upcoming one-time events.</p>
        ) : (
          oneTimeEvents.map((ev) => (
            <div className="event-card" key={ev.id}>
              <h3>{ev.title}</h3>
              <p>{ev.date} at {formatTime(ev.time)} — {barNameById[ev.barId]}</p>
            </div>
          ))
        )}
      </section>
    </>
  );
}
